// src/services/api.js
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const response = await axios.post(`${API_URL}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${refreshToken}` }
        });
        localStorage.setItem('access_token', response.data.access_token);
        originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
        return api(originalRequest);
      } catch {
        localStorage.clear();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

export const clientsApi = {
  getAll: (params) => api.get('/clients', { params: { per_page: 0, ...params } }),
  getById: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  deactivate: (id) => api.post(`/clients/${id}/deactivate`),
  importNistAssessment: (id, formData) => api.post(`/clients/${id}/import-nist-assessment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

export const assessmentsApi = {
  getAll: (params) => api.get('/assessments', { params }),
  getById: (id) => api.get(`/assessments/${id}`),
  create: (data) => api.post('/assessments', data),
  getResponses: (id) => api.get(`/assessments/${id}/responses`),
  createResponse: (assessmentId, data) =>
    api.post(`/assessments/${assessmentId}/responses`, data),
  updateResponse: (assessmentId, responseId, data) =>
    api.put(`/assessments/${assessmentId}/responses/${responseId}`, data),
  submit: (id) => api.post(`/assessments/${id}/submit`),
  reopen: (id, data) => api.post(`/assessments/${id}/reopen`, data),
  duplicate: (id, options) => api.post(`/assessments/${id}/duplicate`, options),
  delete: (id) => api.delete(`/assessments/${id}`),
  updateMetadata: (id, data) => api.put(`/assessments/${id}/metadata`, data),
  compare: (data) => api.post('/assessments/compare', data),
  migrateToCSF20: (assessmentId) => api.post(`/assessments/${assessmentId}/migrate-to-csf-2-0`),
  analyzeCisoSummary: (id) => api.post(`/assessments/${id}/analyze`),
  updateCisoSummary: (id, cisoSummary) => api.put(`/assessments/${id}/ciso-summary`, { ciso_summary: cisoSummary }),
  
  // PDF Generation with custom options
  generatePdf: (assessmentId, queryParams) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'GET',
      url: `${API_URL}/pdf-reports/${assessmentId}/pdf?${queryParams}`,
      headers: {
        'Authorization': `Bearer ${token}`
      },
      responseType: 'blob'
    });
  },

  // CMMC Compliance Reports
  generateSSP: (assessmentId) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'POST',
      url: `${API_URL}/assessments/${assessmentId}/generate-ssp`,
      headers: {
        'Authorization': `Bearer ${token}`
      },
      responseType: 'blob',
      timeout: 180000 // 3 minutes
    });
  },

  generatePOAM: (assessmentId) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'POST',
      url: `${API_URL}/assessments/${assessmentId}/generate-poam`,
      headers: {
        'Authorization': `Bearer ${token}`
      },
      responseType: 'blob',
      timeout: 180000 // 3 minutes
    });
  },

  getCMMCReportStatus: (assessmentId) => {
    return api.get(`/assessments/${assessmentId}/cmmc-report-status`);
  },
};

export const assessmentTypesApi = {
  getAll: () => api.get('/assessment-types'),
  getById: (id) => api.get(`/assessment-types/${id}`),
  getStructure: (id) => api.get(`/assessment-types/${id}/structure`),
};

export const reportsApi = {
  getClientTrending: (clientId, params) => api.get(`/reports/client/${clientId}/trending`, { params }),
  getSummary: (assessmentId) => api.get(`/reports/assessment/${assessmentId}/summary`),
};

export const authApi = {
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  validateResetToken: (token) => api.post('/auth/validate-reset-token', { token }),
};

export const vulnerabilityApi = {
  // Upload and create scan
  uploadScan: (formData) => api.post('/vulnerabilities/scans/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Get scans
  getScans: (params) => api.get('/vulnerabilities/scans', { params }),
  getScan: (scanId) => api.get(`/vulnerabilities/scans/${scanId}`),
  
  // Get findings
  getFindings: (scanId, params) => api.get(`/vulnerabilities/scans/${scanId}/findings`, { params }),
  
  // Ignore functionality
  ignoreFinding: (findingId, reason) => api.post(`/vulnerabilities/findings/${findingId}/ignore`, { reason }),
  unignoreFinding: (findingId) => api.post(`/vulnerabilities/findings/${findingId}/unignore`),
  
  // AI Analysis
  analyzeScan: (scanId) => api.post(`/vulnerabilities/scans/${scanId}/analyze`),
  updateCisoSummary: (scanId, ciso_summary) => api.put(`/vulnerabilities/scans/${scanId}/ciso-summary`, { ciso_summary }),
  
  // Comparison/Trending
  compareScans: (scan_id_1, scan_id_2) => api.post('/vulnerabilities/scans/compare', { scan_id_1, scan_id_2 }),
  
  // Download comparison report
  downloadComparisonReport: (scan_id_1, scan_id_2) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'POST',
      url: `${API_URL}/vulnerabilities/scans/compare-report`,
      headers: { 'Authorization': `Bearer ${token}` },
      data: { scan_id_1, scan_id_2 },
      responseType: 'blob',
      timeout: 60000 // 1 minute
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : 'Vulnerability_Trending_Report.docx';
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    });
  },
  
  // Report generation
  generateReport: (scanId) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'POST',
      url: `${API_URL}/vulnerabilities/scans/${scanId}/generate-report`,
      headers: { 'Authorization': `Bearer ${token}` },
      responseType: 'blob',
      timeout: 60000 // 1 minute
    });
  },
  
  // Delete scan
  deleteScan: (scanId) => api.delete(`/vulnerabilities/scans/${scanId}`),
};

// ==================== ACTIVE DIRECTORY AUDIT API ====================
export const adAuditApi = {
  // Upload one or more files to create an audit record
  uploadAudit: (formData) => api.post('/active-directory/audits/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // List audits with optional client_id / status filter
  getAudits: (params) => api.get('/active-directory/audits', { params }),

  // Get single audit with embedded analysis
  getAudit: (auditId) => api.get(`/active-directory/audits/${auditId}`),

  // Get findings with optional severity / category / include_ignored filters
  getFindings: (auditId, params) => api.get(`/active-directory/audits/${auditId}/findings`, { params }),

  // Ignore / un-ignore individual findings
  ignoreFinding: (findingId, reason) =>
    api.post(`/active-directory/findings/${findingId}/ignore`, { reason }),
  unignoreFinding: (findingId) =>
    api.post(`/active-directory/findings/${findingId}/unignore`),

  // Run Claude AI analysis on the uploaded documents (allow up to 4 min for large docs)
  analyzeAudit: (auditId) => api.post(`/active-directory/audits/${auditId}/analyze`, {}, { timeout: 240000 }),

  // Update CISO executive summary (after editing)
  updateCisoSummary: (auditId, ciso_summary) =>
    api.put(`/active-directory/audits/${auditId}/ciso-summary`, { ciso_summary }),

  // Compare two audits (same client)
  compareAudits: (audit_id_1, audit_id_2) =>
    api.post('/active-directory/audits/compare', { audit_id_1, audit_id_2 }),

  // Download comparison DOCX report
  downloadComparisonReport: (audit_id_1, audit_id_2) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'POST',
      url: `${API_URL}/active-directory/audits/compare-report`,
      headers: { 'Authorization': `Bearer ${token}` },
      data: { audit_id_1, audit_id_2 },
      responseType: 'blob',
      timeout: 60000
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'];
      const filename = cd
        ? cd.split('filename=')[1].replace(/"/g, '')
        : `AD_Audit_Trending_Report_${Date.now()}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
  },

  // Generate & download single audit DOCX report
  generateReport: (auditId) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'POST',
      url: `${API_URL}/active-directory/audits/${auditId}/generate-report`,
      headers: { 'Authorization': `Bearer ${token}` },
      responseType: 'blob',
      timeout: 60000
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'];
      const filename = cd
        ? cd.split('filename=')[1].replace(/"/g, '')
        : `AD_Audit_Report_${Date.now()}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
  },

  // Delete an audit and all associated data
  deleteAudit: (auditId) => api.delete(`/active-directory/audits/${auditId}`)
};

// ==================== PENETRATION TESTING API ====================
export const adminApi = {
  getAllowedIps: () => api.get('/admin/allowed-ips'),
  createAllowedIp: (data) => api.post('/admin/allowed-ips', data),
  updateAllowedIp: (id, data) => api.put(`/admin/allowed-ips/${id}`, data),
  deleteAllowedIp: (id) => api.delete(`/admin/allowed-ips/${id}`),
};

export const pentestApi = {
  // Get all penetration tests - uses 'list' to match PenetrationTests.jsx
  list: (params) => api.get('/pentests', { params }),
  
  // Get single penetration test
  getById: (pentestId) => api.get(`/pentests/${pentestId}`),
  
  // Upload and parse technical report
  uploadReport: (formData) => api.post('/pentests/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  
  // Delete penetration test
  delete: (pentestId) => api.delete(`/pentests/${pentestId}`),
  
  // Get findings for a penetration test
  getFindings: (pentestId, includeIgnored = false) => 
    api.get(`/pentests/${pentestId}/findings`, { params: { include_ignored: includeIgnored } }),
  
  // Ignore/unignore findings
  ignoreFinding: (findingId, reason) => 
    api.post(`/pentests/findings/${findingId}/ignore`, { reason }),
  
  unignoreFinding: (findingId) => 
    api.post(`/pentests/findings/${findingId}/unignore`),
  
  // AI Analysis
  analyze: (pentestId) => api.post(`/pentests/${pentestId}/analyze`),
  
  updateCisoSummary: (pentestId, executive_summary) => 
    api.put(`/pentests/${pentestId}/ciso-summary`, { executive_summary }),
  
  updatePriorities: (pentestId, critical_priorities) => 
    api.put(`/pentests/${pentestId}/priorities`, { critical_priorities }),
  
  updateObservations: (pentestId, positive_observations) => 
    api.put(`/pentests/${pentestId}/observations`, { positive_observations }),
  
  // Generate CISO report
  generateReport: (pentestId) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'POST',
      url: `${API_URL}/pentests/${pentestId}/generate-report`,
      headers: { 'Authorization': `Bearer ${token}` },
      responseType: 'blob',
      timeout: 60000 // 1 minute
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `CISO_Summary_${Date.now()}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
  },
  
  // Compare two penetration tests
  compare: (pentest_id_1, pentest_id_2) =>
    api.post('/pentests/compare', { pentest_id_1, pentest_id_2 }),

  // Download trending comparison report
  downloadComparisonReport: (pentest_id_1, pentest_id_2) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'POST',
      url: `${API_URL}/pentests/compare-report`,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      data: { pentest_id_1, pentest_id_2 },
      responseType: 'blob',
      timeout: 60000 // 1 minute
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const contentDisposition = response.headers['content-disposition'];
      const filename = contentDisposition
        ? contentDisposition.split('filename=')[1].replace(/"/g, '')
        : `Trending_Report_${Date.now()}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
  }
};

// ── Value of Risk (VoR) API ───────────────────────────────────────────────────

export const vorApi = {
  // Assessments
  listAssessments:  (params) => api.get('/vor/assessments/', { params }),
  getAssessment:    (id)     => api.get(`/vor/assessments/${id}`),
  createAssessment: (data)   => api.post('/vor/assessments/', data),
  updateAssessment: (id, data) => api.put(`/vor/assessments/${id}`, data),
  deleteAssessment: (id)     => api.delete(`/vor/assessments/${id}`),

  // Step saves
  saveRiskTolerance: (id, data) => api.post(`/vor/assessments/${id}/risk-tolerance`, data),
  saveCostInputs:    (id, data) => api.post(`/vor/assessments/${id}/cost-inputs`, data),
  saveCredibleRisks: (id, data) => api.post(`/vor/assessments/${id}/credible-risks`, data),
  saveControls:      (id, data) => api.post(`/vor/assessments/${id}/controls`, data),
  calculate:         (id)       => api.post(`/vor/assessments/${id}/calculate`),

  // Report download (returns Word doc blob)
  downloadReport: (id, clientName) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'POST',
      url: `${API_URL}/vor/reports/${id}/generate`,
      headers: { 'Authorization': `Bearer ${token}` },
      responseType: 'blob',
      timeout: 60000,
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'];
      const filename = cd
        ? cd.split('filename=')[1]?.replace(/"/g, '')
        : `VoR_Report_${clientName || id}_${Date.now()}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
  },

  // Catalog
  listRisks:      () => api.get('/vor/catalog/risks'),
  createRisk:     (data) => api.post('/vor/catalog/risks', data),
  updateRisk:     (id, data) => api.put(`/vor/catalog/risks/${id}`, data),
  deleteRisk:     (id) => api.delete(`/vor/catalog/risks/${id}`),

  listControls:   () => api.get('/vor/catalog/controls'),
  createControl:  (data) => api.post('/vor/catalog/controls', data),
  updateControl:  (id, data) => api.put(`/vor/catalog/controls/${id}`, data),
  deleteControl:  (id) => api.delete(`/vor/catalog/controls/${id}`),

  listMappings:   () => api.get('/vor/catalog/control-risk-mappings'),
  createMapping:  (data) => api.post('/vor/catalog/control-risk-mappings', data),
  updateMapping:  (id, data) => api.put(`/vor/catalog/control-risk-mappings/${id}`, data),
  deleteMapping:  (id) => api.delete(`/vor/catalog/control-risk-mappings/${id}`),

  listQuestions:  () => api.get('/vor/catalog/risk-tolerance-questions'),
  createQuestion: (data) => api.post('/vor/catalog/risk-tolerance-questions', data),
  updateQuestion: (id, data) => api.put(`/vor/catalog/risk-tolerance-questions/${id}`, data),
  deleteQuestion: (id) => api.delete(`/vor/catalog/risk-tolerance-questions/${id}`),

  // Dashboard
  dashboardStats: () => api.get('/vor/dashboard/stats'),
};

// ── Network Admin Review (NAR) API ────────────────────────────────────────────

const _blobDownload = (promise, fallbackName) =>
  promise.then(response => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    const cd = response.headers['content-disposition'];
    const filename = cd ? cd.split('filename=')[1]?.replace(/"/g, '') : fallbackName;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  });

// ── Dark Web Exposures API ─────────────────────────────────────────────────────
export const darkWebApi = {
  list: (params) => api.get('/integrations/blackpoint/dark-web', { params }),
  get:  (id)     => api.get(`/integrations/blackpoint/dark-web/${id}`),
  sync: (full = false) => api.post('/integrations/blackpoint/dark-web/sync', { full }),

  downloadReport: (clientId) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'GET',
      url: `${API_URL}/integrations/blackpoint/dark-web/report/${clientId}`,
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
      timeout: 120000,
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'];
      const filename = cd
        ? cd.split('filename=')[1]?.replace(/"/g, '')
        : `DarkWeb_Report_${clientId}_${Date.now()}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
  },
};

// ── Ticket Schedules ───────────────────────────────────────────────────────────
export const ticketSchedulesApi = {
  list:     (clientId)    => api.get('/ticket-schedules', { params: { client_id: clientId } }),
  get:      (id)          => api.get(`/ticket-schedules/${id}`),
  create:   (data)        => api.post('/ticket-schedules', data),
  update:   (id, data)    => api.put(`/ticket-schedules/${id}`, data),
  delete:   (id)          => api.delete(`/ticket-schedules/${id}`),
  toggle:   (id)          => api.post(`/ticket-schedules/${id}/toggle`),
  runNow:   (id)          => api.post(`/ticket-schedules/${id}/run-now`),
};

// ── ConnectWise Cascade Pickers ────────────────────────────────────────────────
export const cwApi = {
  getBoards:       ()        => api.get('/integrations/connectwise/service-boards'),
  // getBoardOptions returns statuses, types, subtypes, items, and itemsBySubtype in one call.
  // Use this instead of the individual type/subtype/item endpoints in cascade pickers.
  getBoardOptions: (boardId) => api.get(`/integrations/connectwise/service-boards/${boardId}/options`),
  getStatuses:     (boardId) => api.get(`/integrations/connectwise/service-boards/${boardId}/statuses`),
  getMembers:      ()        => api.get('/integrations/connectwise/members'),
};

export const narApi = {
  // Assessments
  listNars:      (params) => api.get('/nar/assessments/', { params }),
  getNar:        (id)     => api.get(`/nar/assessments/${id}`),
  createNar:     (data)   => api.post('/nar/assessments/', data),
  updateNar:     (id, data) => api.put(`/nar/assessments/${id}`, data),
  deleteNar:     (id)     => api.delete(`/nar/assessments/${id}`),
  getAnswers:    (id)     => api.get(`/nar/assessments/${id}/answers`),
  saveAnswers:   (id, answers) => api.put(`/nar/assessments/${id}/answers`, answers),
  getScore:      (id)     => api.get(`/nar/assessments/${id}/score`),
  submitNar:     (id)     => api.post(`/nar/assessments/${id}/submit`),
  reopenNar:     (id)     => api.post(`/nar/assessments/${id}/reopen`),
  duplicateNar:  (id, data) => api.post(`/nar/assessments/${id}/duplicate`, data),
  generateSummary: (id)  => api.post(`/nar/assessments/${id}/generate-summary`),
  getTrending:   (params) => api.get('/nar/assessments/trending', { params }),
  exportAnswers: (id, name) => _blobDownload(
    axios({ method: 'GET', url: `${API_URL}/nar/assessments/${id}/export-answers`,
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
            responseType: 'blob', timeout: 60000 }),
    `NAR_${name || id}.xlsx`
  ),
  importAnswers: (id, formData) =>
    api.post(`/nar/assessments/${id}/import-answers`, formData,
             { headers: { 'Content-Type': 'multipart/form-data' } }),

  // Reports
  getReportData: (id) => api.get(`/nar/reports/${id}/data`),
  downloadPdf:   (id, name) => _blobDownload(
    axios({ method: 'GET', url: `${API_URL}/nar/reports/${id}/pdf`,
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
            responseType: 'blob', timeout: 120000 }),
    `NAR_Report_${name || id}.pdf`
  ),
  downloadXlsx:  (id, name) => _blobDownload(
    axios({ method: 'GET', url: `${API_URL}/nar/reports/${id}/xlsx`,
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
            responseType: 'blob', timeout: 60000 }),
    `NAR_${name || id}.xlsx`
  ),

  // Catalog — types & structure
  listTypes:       ()       => api.get('/nar/catalog/types'),
  getTypeStructure:(id)     => api.get(`/nar/catalog/types/${id}/structure`),
  createType:      (data)   => api.post('/nar/catalog/types', data),
  updateType:      (id, d)  => api.put(`/nar/catalog/types/${id}`, d),

  // Catalog — sections
  listSections:    (params) => api.get('/nar/catalog/sections', { params }),
  createSection:   (data)   => api.post('/nar/catalog/sections', data),
  updateSection:   (id, d)  => api.put(`/nar/catalog/sections/${id}`, d),

  // Catalog — categories
  listCategories:  (params) => api.get('/nar/catalog/categories', { params }),
  createCategory:  (data)   => api.post('/nar/catalog/categories', data),
  updateCategory:  (id, d)  => api.put(`/nar/catalog/categories/${id}`, d),

  // Catalog — questions
  listQuestions:   (params) => api.get('/nar/catalog/questions', { params }),
  createQuestion:  (data)   => api.post('/nar/catalog/questions', data),
  updateQuestion:  (id, d)  => api.put(`/nar/catalog/questions/${id}`, d),
  setQuestionTags: (id, tag_ids) => api.put(`/nar/catalog/questions/${id}/tags`, { tag_ids }),

  // Catalog — tags
  listTags:    ()      => api.get('/nar/catalog/tags'),
  createTag:   (data)  => api.post('/nar/catalog/tags', data),
  updateTag:   (id, d) => api.put(`/nar/catalog/tags/${id}`, d),
  deleteTag:   (id)    => api.delete(`/nar/catalog/tags/${id}`),

  // Catalog — grade scale
  getGradeScale:   ()     => api.get('/nar/catalog/grade-scale'),
  updateGradeScale:(items) => api.put('/nar/catalog/grade-scale', items),

  // Catalog — bulk import
  downloadImportTemplate: (typeId) => _blobDownload(
    axios({ method: 'GET', url: `${API_URL}/nar/catalog/types/${typeId}/import-template`,
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
            responseType: 'blob' }),
    'nar_question_import_template.xlsx'
  ),
  importQuestions: (typeId, formData) =>
    api.post(`/nar/catalog/types/${typeId}/import-questions`, formData,
             { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// ── AI Governance API ──────────────────────────────────────────────────────────
export const aiGovApi = {
  // Tiers
  getTiers: () => api.get('/ai-gov/tiers'),
  updateTier: (tierId, data) => api.put(`/ai-gov/tiers/${tierId}`, data),
  billingPreview: (tier_id, billing_cycle) =>
    api.post('/ai-gov/billing-preview', { tier_id, billing_cycle }),

  // Programs
  listPrograms: () => api.get('/ai-gov/programs'),
  getProgram: (id) => api.get(`/ai-gov/programs/${id}`),
  getProgramByClient: (clientId) => api.get(`/ai-gov/programs/by-client/${clientId}`),
  createProgram: (data) => api.post('/ai-gov/programs', data),
  updateProgram: (id, data) => api.put(`/ai-gov/programs/${id}`, data),

  // Assessment trigger
  startAssessment: (programId, assessment_type) =>
    api.post(`/ai-gov/programs/${programId}/start-assessment`, { assessment_type }),

  // Templates
  listTemplates: (active_only = true) =>
    api.get('/ai-gov/templates', { params: { active: active_only } }),
  uploadTemplate: (formData) =>
    api.post('/ai-gov/templates', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateTemplate: (id, data) => api.put(`/ai-gov/templates/${id}`, data),
  scanTemplateFields: (id) => api.post(`/ai-gov/templates/${id}/scan-fields`),
  deleteTemplate: (id) => api.delete(`/ai-gov/templates/${id}`),

  // Documents
  generateDocument: (programId, template_id) =>
    api.post(`/ai-gov/programs/${programId}/generate-document`, { template_id }),
  updateDocumentStatus: (docId, status) =>
    api.put(`/ai-gov/documents/${docId}/status`, { status }),
  downloadDocument: (docId) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'GET',
      url: `${API_URL}/ai-gov/documents/${docId}/download`,
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
      timeout: 30000,
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'];
      const filename = cd
        ? cd.split('filename=')[1]?.replace(/"/g, '')
        : `AIGov_Document_${docId}.docx`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    });
  },

  // Cadence tasks
  listTasks: (programId) => api.get(`/ai-gov/programs/${programId}/tasks`),
  completeTask: (programId, taskId, notes) =>
    api.post(`/ai-gov/programs/${programId}/tasks/${taskId}/complete`, { notes }),
  getTaskSchedule: (programId, taskId) =>
    api.get(`/ai-gov/programs/${programId}/tasks/${taskId}/schedule`),
  upsertTaskSchedule: (programId, taskId, data) =>
    api.post(`/ai-gov/programs/${programId}/tasks/${taskId}/schedule`, data),
  deleteTaskSchedule: (programId, taskId) =>
    api.delete(`/ai-gov/programs/${programId}/tasks/${taskId}/schedule`),

  generateOverviewReport: (programId) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'GET',
      url: `${API_URL}/ai-gov/programs/${programId}/overview-report`,
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
      timeout: 60000,
    });
  },

  generateUmbrellaReport: (programId, fromVal, toVal, categoryIds) => {
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams({ from: fromVal, to: toVal });
    if (categoryIds && categoryIds.length > 0) params.set('categories', categoryIds.join(','));
    return axios({
      method: 'GET',
      url: `${API_URL}/ai-gov/programs/${programId}/umbrella-report?${params}`,
      headers: { Authorization: `Bearer ${token}` },
      responseType: 'blob',
      timeout: 120000,
    });
  },

  // Umbrella
  listUmbrellaOrgs: () => api.get('/ai-gov/umbrella/orgs'),
  getUmbrellaCategories: () => api.get('/ai-gov/umbrella/categories'),
  getUmbrellaActivity: (orgId, params = {}) => api.get(`/ai-gov/umbrella/orgs/${orgId}/activity`, { params }),
  getUmbrellaDestinations: (orgId) => api.get(`/ai-gov/umbrella/orgs/${orgId}/destinations`),
  getUmbrellaAiApps: (params = {}) => api.get('/ai-gov/umbrella/ai-apps', { params }),
  getUmbrellaCategoryByOrg: (params = {}) => api.get('/ai-gov/umbrella/provider/category-by-org', { params }),
  getUmbrellaIdentitySummary: (orgId, params = {}) => api.get(`/ai-gov/umbrella/orgs/${orgId}/identity-summary`, { params }),
  getUmbrellaUserActivity: (orgId, params = {}) => api.get(`/ai-gov/umbrella/orgs/${orgId}/user-activity`, { params }),
  deleteDocument: (docId) => api.delete(`/ai-gov/documents/${docId}`),

  getToolRegistry: (includeInactive) => api.get('/ai-gov/tool-registry' + (includeInactive ? '?all=1' : '')),
  createRegistryTool: (data) => api.post('/ai-gov/tool-registry', data),
  updateRegistryTool: (id, data) => api.put(`/ai-gov/tool-registry/${id}`, data),
  deactivateRegistryTool: (id) => api.delete(`/ai-gov/tool-registry/${id}`),
  restoreRegistryTool: (id) => api.delete(`/ai-gov/tool-registry/${id}`, { data: { restore: true } }),
  syncToolRegistry: () => api.post('/ai-gov/tool-registry/sync'),
  getObservedDomains: () => api.get('/ai-gov/tool-registry/observed-domains'),
  getAiToolCatalog: () => api.get('/ai-gov/catalog/ai-tools'),
  listApprovedTools: (programId) => api.get(`/ai-gov/programs/${programId}/approved-tools`),
  addApprovedTool: (programId, data) => api.post(`/ai-gov/programs/${programId}/approved-tools`, data),
  updateApprovedTool: (programId, toolId, data) => api.put(`/ai-gov/programs/${programId}/approved-tools/${toolId}`, data),
  patchApprovedTool: (programId, toolId, data) => api.patch(`/ai-gov/programs/${programId}/approved-tools/${toolId}`, data),
  deleteApprovedTool: (programId, toolId) => api.delete(`/ai-gov/programs/${programId}/approved-tools/${toolId}`),
};

// ── Annual Security Review API ────────────────────────────────────────────────
export const annualReviewApi = {
  getClientData: (clientId) => api.get(`/annual-security-review/client-data/${clientId}`),
  analyze: (clientId, selectedSections) =>
    api.post(`/annual-security-review/analyze/${clientId}`, { selected_sections: selectedSections }),
  saveSummary: (clientId, cisoSummary, selectedSections) =>
    api.put(`/annual-security-review/save-summary/${clientId}`, { ciso_summary: cisoSummary, selected_sections: selectedSections }),
  generatePdf: (clientId, selectedSections, cisoSummary) => {
    const token = localStorage.getItem('access_token');
    return axios({
      method: 'POST',
      url: `${API_URL}/annual-security-review/pdf/${clientId}`,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      data: { selected_sections: selectedSections, ciso_summary: cisoSummary },
      responseType: 'blob',
      timeout: 120000,
    });
  },
};
